import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  IconButton,
  Box,
  Slide,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import WeatherCard from './WeatherCard';

/**
 * WeatherModal Component
 * 
 * Displays detailed weather information in a modal overlay.
 * Slides in from the side where the weather icon was clicked.
 * 
 * Features:
 * - Slide animation from left (departure) or right (arrival)
 * - Full WeatherCard component with all details
 * - Close button (X) to dismiss
 * - Centered overlay on hero card
 * - Responsive design
 * 
 * Props:
 * - isOpen: boolean - Controls modal visibility
 * - onClose: function - Callback when modal closes
 * - airportCode: string - ICAO airport code (e.g., "KJFK")
 * - slideFrom: 'left' | 'right' - Side to slide from
 * - title: string - Optional title override
 * 
 * TODO: Wire up to real weather API when backend integration starts
 * Currently uses fake weather data from WeatherCard component
 * 
 * @component
 */
const WeatherModal = ({ isOpen, onClose, airportCode, slideFrom = 'left', title }) => {
  // Slide direction for animation
  const slideDirection = slideFrom === 'left' ? 'right' : 'left';

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      TransitionComponent={Slide}
      TransitionProps={{
        direction: slideDirection,
      }}
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          margin: 2,
          maxHeight: 'calc(100vh - 32px)',
        },
      }}
    >
      {/* Close Button */}
      <Box
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          zIndex: 1,
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
            color: 'white',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Weather Card Content */}
      <DialogContent sx={{ padding: 0 }}>
        <WeatherCard
          icao={airportCode}
          title={title || `${airportCode} Weather`}
          autoRefresh={false} // Don't auto-refresh in modal
        />
      </DialogContent>
    </Dialog>
  );
};

export default WeatherModal;
